import { useState, useEffect } from 'react';
import { tenantsAPI } from '../services/api';
import { Building2, Plus, Edit2, ToggleLeft, ToggleRight } from 'lucide-react';
import toast from 'react-hot-toast';

function TenantModal({ tenant, onClose, onSaved }) {
  const [form, setForm] = useState(tenant || { name: '', slug: '', plan: 'free' });
  const [loading, setLoading] = useState(false);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const autoSlug = (name) => {
    set('slug', name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, ''));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (tenant) {
        await tenantsAPI.update(tenant.id, { name: form.name, plan: form.plan });
      } else {
        await tenantsAPI.create(form);
      }
      toast.success(tenant ? 'Brand updated' : 'Brand created');
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error saving brand');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal">
        <div className="modal-header">
          <h2 className="modal-title">{tenant ? 'Edit Brand' : 'New Brand'}</h2>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div className="form-group">
            <label className="form-label">Brand Name</label>
            <input className="form-input" value={form.name} required
              onChange={e => { set('name', e.target.value); if (!tenant) autoSlug(e.target.value); }}
              placeholder="e.g. Woolworths" />
          </div>
          {!tenant && (
            <div className="form-group">
              <label className="form-label">Slug (URL identifier)</label>
              <input className="form-input" value={form.slug} required
                onChange={e => set('slug', e.target.value)}
                placeholder="e.g. woolworths" />
            </div>
          )}
          <div className="form-group">
            <label className="form-label">Plan</label>
            <select className="form-select" value={form.plan} onChange={e => set('plan', e.target.value)}>
              <option value="free">Free</option>
              <option value="paid_standard">Paid Standard</option>
              <option value="enterprise">Enterprise</option>
            </select>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-ghost" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? 'Saving...' : tenant ? 'Save Changes' : 'Create Brand'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function Tenants() {
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null); // null | 'new' | tenant object

  const load = () => {
    setLoading(true);
    tenantsAPI.list().then(r => setTenants(r.data)).finally(() => setLoading(false));
  };

  useEffect(load, []);

  const toggleActive = async (t) => {
    try {
      await tenantsAPI.update(t.id, { is_active: !t.is_active });
      toast.success(`Brand ${t.is_active ? 'deactivated' : 'activated'}`);
      load();
    } catch { toast.error('Failed to update'); }
  };

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Brands</h1>
          <p className="page-subtitle">Manage your client tenants and their plan tiers</p>
        </div>
        <button className="btn btn-primary" onClick={() => setModal('new')}>
          <Plus size={16} /> New Brand
        </button>
      </div>

      <div className="card">
        {loading ? (
          <div className="loading-state"><div className="spinner" /></div>
        ) : tenants.length === 0 ? (
          <div className="empty-state">
            <Building2 size={48} />
            <h3>No brands yet</h3>
            <p>Create your first brand to get started</p>
            <button className="btn btn-primary" style={{ marginTop: 16 }} onClick={() => setModal('new')}>
              <Plus size={16} /> Create Brand
            </button>
          </div>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Brand</th>
                  <th>Slug</th>
                  <th>Plan</th>
                  <th>Status</th>
                  <th>Created</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {tenants.map(t => (
                  <tr key={t.id}>
                    <td style={{ fontWeight: 600 }}>{t.name}</td>
                    <td><code style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{t.slug}</code></td>
                    <td>
                      <span className={`badge badge-${t.plan === 'free' ? 'grey' : t.plan === 'enterprise' ? 'purple' : 'green'}`}>
                        {t.plan}
                      </span>
                    </td>
                    <td>
                      <span className={`badge badge-${t.is_active ? 'green' : 'red'}`}>
                        <span className={`dot dot-${t.is_active ? 'green' : 'red'}`} style={{ width: 6, height: 6 }} />
                        {t.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td style={{ color: 'var(--text-secondary)', fontSize: 13 }}>
                      {new Date(t.created_at).toLocaleDateString()}
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: 6 }}>
                        <button className="btn btn-ghost btn-sm" onClick={() => setModal(t)}>
                          <Edit2 size={12} /> Edit
                        </button>
                        <button className={`btn btn-sm btn-${t.is_active ? 'danger' : 'success'}`} onClick={() => toggleActive(t)}>
                          {t.is_active ? <ToggleRight size={12} /> : <ToggleLeft size={12} />}
                          {t.is_active ? 'Deactivate' : 'Activate'}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {modal && (
        <TenantModal
          tenant={modal === 'new' ? null : modal}
          onClose={() => setModal(null)}
          onSaved={() => { setModal(null); load(); }}
        />
      )}
    </div>
  );
}
